/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { Epic } from 'redux-observable';
import { Create, Request } from '../EpicUtils';
import { CONFIGURE_GOOGLE_FOR_SITE, getGtmDataByAccount } from '../../actions/Google';
import GoogleService from '../../../api/services/GoogleService';
import { getTokenString, isGA4property } from '../../../utils/Google';
import {
  ALL_EVENTS_TRIGGER_DEFAULT,
  ALL_PAGES_TRIGGER_DEFAULT, GA4_TAG_NAME,
  GAS_VARIABLE_NAME,
  getGASVariable,
  getMapsApiKeyVariable,
  GTM_CONTAINER_NAME_DEFAULT,
  MAPS_API_KEY_NAME,
  MAPS_PAGE_TRIGGER_DEFAULT, UA_TAG_NAME,
} from '../../../constant/Google';
import { SiteValues } from '../../../models/Sites';
import { updateCollegeSites } from '../../actions/Sites';

const request: Request<SiteValues, SiteValues> = {
  type: CONFIGURE_GOOGLE_FOR_SITE,
  getData: async (site, state) => {
    const parsedSite = {
      ...site,
      gtmContainerId: site.gtmContainerId === 'new' ? null : site.gtmContainerId,
      gaWebPropertyId: site.gaWebPropertyId === 'new' ? null : site.gaWebPropertyId,
      gaWebStreamId: site.gaWebStreamId === 'new' ? null : site.gaWebStreamId,
    };

    const token = getTokenString(state.google);

    if (parsedSite.gtmAccountId) {
      let { gaWebPropertyId, gaWebStreamId } = parsedSite;

      const isGA4 = isGA4property(gaWebPropertyId);

      // Process GTM container
      let gtmContainerId = state.google.gtmContainers[parsedSite.gtmAccountId].find((c) => c.publicId === parsedSite.gtmContainerId)?.containerId;

      if (!gtmContainerId) {
        await GoogleService.createGTMContainer(
          token,
          parsedSite.gtmAccountId,
          {
            name: GTM_CONTAINER_NAME_DEFAULT,
            usageContext: [
              'web'
            ]
          }
        ).then((res) => {
          gtmContainerId = res.containerId;
        });
      }

      const workspaces = await GoogleService.getGTMWorkspaces(
        token,
        parsedSite.gtmAccountId,
        gtmContainerId
      );

      const workspace = workspaces.workspace[0].workspaceId;

      const preview = await GoogleService.getGTMPreview(
        token,
        parsedSite.gtmAccountId,
        gtmContainerId,
        workspace
      );

      const GA4Tag = preview.containerVersion?.tag?.find(t => t.name === GA4_TAG_NAME);
      const GA4Parameter =  GA4Tag?.parameter?.find(p => p.key === 'measurementId');
      
      // Process web property
      if (site.gaWebPropertyId) {
        if (site.gaWebPropertyId === 'new') {
          await GoogleService.createGAProperty(
            token,
            parsedSite.googleAnalyticsId,
            {
              name: `${parsedSite.name} web property`
            }
          ).then((res) => {
            gaWebPropertyId = res.id;
          });

          await GoogleService.createGAProfile(
            token,
            parsedSite.googleAnalyticsId,
            gaWebPropertyId,
            {
              name: `${parsedSite.name} profile`
            }
          );
        }
        
        if (gaWebStreamId && GA4Parameter && GA4Parameter.value !== gaWebStreamId) {
          await GoogleService.updateGTMTag(
            token,
            parsedSite.gtmAccountId,
            gtmContainerId,
            workspace,
            {
              ...GA4Tag,
              parameter: GA4Tag.parameter.map(p => ({
                ...p,
                value: p.key === 'measurementId' ? gaWebStreamId : p.value
              }))
            }
          );
        }
      }

      if (site.gaWebStreamId === 'new' && gaWebPropertyId && isGA4) {
        await GoogleService.createGAWebStream(
          token,
          gaWebPropertyId,
          {
            type: 'WEB_DATA_STREAM',
            displayName: `${parsedSite.name} web stream`,
            webStreamData: {
              defaultUri: `https://${parsedSite.key}.oncourse.cc`
            }
          }
        ).then((res) => {
          gaWebStreamId = res.id;
        });
      }

      const gasVariable = preview.containerVersion.variable?.find((v) => v.type === 'gas' && v.name === GAS_VARIABLE_NAME);

      if (!gasVariable && parsedSite.googleAnalyticsId && gaWebPropertyId && !isGA4) {
        await GoogleService.createGTMVariable(
          token,
          parsedSite.gtmAccountId,
          gtmContainerId,
          workspace,
          getGASVariable(gaWebPropertyId)
        );
      } else if (gasVariable && parsedSite.googleAnalyticsId && gaWebPropertyId && !isGA4) {
        await GoogleService.updateGTMVariable(
          token,
          parsedSite.gtmAccountId,
          gtmContainerId,
          workspace,
          {
            ...gasVariable,
            ...getGASVariable(gaWebPropertyId)
          }
        );
      }

      const trigger_all_pages = preview.containerVersion.trigger?.find((t) => t.name === ALL_PAGES_TRIGGER_DEFAULT.name)
        || await GoogleService.createGTMTrigger(
          token,
          parsedSite.gtmAccountId,
          gtmContainerId,
          workspace,
          ALL_PAGES_TRIGGER_DEFAULT
        );

      const trigger_all_events = preview.containerVersion.trigger?.find((t) => t.name === ALL_EVENTS_TRIGGER_DEFAULT.name)
        || !isGA4 && await GoogleService.createGTMTrigger(
          token,
          parsedSite.gtmAccountId,
          gtmContainerId,
          workspace,
          ALL_EVENTS_TRIGGER_DEFAULT
        );

      const trigger_maps_page = preview.containerVersion.trigger?.find((t) => t.name === MAPS_PAGE_TRIGGER_DEFAULT.name)
        || await GoogleService.createGTMTrigger(
          token,
          parsedSite.gtmAccountId,
          gtmContainerId,
          workspace,
          MAPS_PAGE_TRIGGER_DEFAULT
        );

      const hasUATag = preview.containerVersion?.tag?.find(t => t.name === UA_TAG_NAME);

      if (!hasUATag && !gaWebStreamId) {
        await GoogleService.createGTMTag(
          token,
          parsedSite.gtmAccountId,
          gtmContainerId,
          workspace,
          {
            type: 'ua',
            name: UA_TAG_NAME,
            firingTriggerId: [trigger_all_pages?.triggerId],
            tagFiringOption: 'oncePerEvent',
            parameter: [
              {
                key: 'overrideGaSettings',
                type: 'boolean',
                value: 'false'
              },
              {
                key: 'trackType',
                type: 'template',
                value: 'TRACK_PAGEVIEW'
              },
              {
                key: 'gaSettings',
                type: 'template',
                value: '{{ish onCourse Google Analytics settings}}'
              }
            ],
          }
        );

        await GoogleService.createGTMTag(
          token,
          parsedSite.gtmAccountId,
          gtmContainerId,
          workspace,
          {
            type: 'ua',
            name: 'ish onCourse Google Analytics events',
            tagFiringOption: 'oncePerEvent',
            firingTriggerId: [trigger_all_events.triggerId],
            parameter: [
              {
                key: 'overrideGaSettings',
                type: 'boolean',
                value: 'false'
              },
              {
                key: 'trackType',
                type: 'template',
                value: 'TRACK_EVENT'
              },
              {
                key: 'gaSettings',
                type: 'template',
                value: '{{ish onCourse Google Analytics settings}}'
              }
            ]
          }
        );
      }

      if (gaWebStreamId && (site.gtmContainerId === 'new' || !preview.containerVersion?.tag?.find(t => t.type === 'gaawc'))) {
        await GoogleService.createGTMTag(
          token,
          parsedSite.gtmAccountId,
          gtmContainerId,
          workspace,
          {
            type: 'gaawc',
            name: GA4_TAG_NAME,
            firingTriggerId: [trigger_all_pages?.triggerId],
            parameter: [
              {
                'type': 'boolean',
                'key': 'sendPageView',
                'value': 'true'
              },
              {
                'type': 'boolean',
                'key': 'enableSendToServerContainer',
                'value': 'false'
              },
              {
                'type': 'template',
                'key': 'measurementId',
                'value': gaWebStreamId
              }
            ]
          }
        );
      }

      if (parsedSite.googleMapsApiKey) {

        const mapApiKeyVariable = preview.containerVersion.variable?.find((v) => v.type === 'c' && v.name === MAPS_API_KEY_NAME);

        if (!mapApiKeyVariable) {
          await GoogleService.createGTMVariable(
            token,
            parsedSite.gtmAccountId,
            gtmContainerId,
            workspace,
            getMapsApiKeyVariable(parsedSite.googleMapsApiKey)
          );
        } else {
          await GoogleService.updateGTMVariable(
            token,
            parsedSite.gtmAccountId,
            gtmContainerId,
            workspace,
            {
              ...getMapsApiKeyVariable(parsedSite.googleMapsApiKey),
              variableId: mapApiKeyVariable.variableId
            }
          );
        }

        const mapApiKeyTag = preview.containerVersion?.tag.find(t => t.name === 'Google maps')

        if (!mapApiKeyTag) {
          await GoogleService.createGTMTag(
            token,
            parsedSite.gtmAccountId,
            gtmContainerId,
            workspace,
            {
              name: 'Google maps',
              firingTriggerId: [trigger_maps_page.triggerId],
              type: 'html',
              tagFiringOption: 'ONCE_PER_LOAD',
              parameter: [
                {
                  type: 'TEMPLATE',
                  key: 'html',
                  value: `<script src="https://maps.googleapis.com/maps/api/js?key={{${MAPS_API_KEY_NAME}}}&v=3.exp&callback=initMaps"></script>`
                },
                {
                  type: 'BOOLEAN',
                  key: 'supportDocumentWrite',
                  value: 'false'
                }
              ],
            }
          );
        }
      }

      await GoogleService.publishGTM(
        token,
        parsedSite.gtmAccountId,
        gtmContainerId,
        workspace,
      );
    }

    return parsedSite;
  },
  processData: ({
    collegeKey,
    googleMapsApiKey,
    gaWebPropertyId,
    gaWebStreamId,
    ...site
  }) => [
    updateCollegeSites({ changed: [site] }),
    getGtmDataByAccount(site.gtmAccountId)
  ]
};

export const EpicConfigGoogleServicesForSite: Epic<any, any> = Create(request);